<script>
	import Titlebar from "./components/Titlebar.svelte";
	import Sidebar from "./components/Sidebar.svelte";
	import LoginView from "./views/LoginView.svelte";
	import SubscriptionBlockedView from "./views/SubscriptionBlockedView.svelte";
	import KanbanView from "./views/KanbanView.svelte";
	import WhatsAppView from "./views/WhatsAppView.svelte";
	import AgentFunnelView from "./views/AgentFunnelView.svelte";
	import PrinterView from "./views/PrinterView.svelte";
	import MenuView from "./views/MenuView.svelte";
	import LoyaltyView from "./views/LoyaltyView.svelte";
	import CashierView from "./views/CashierView.svelte";
	import ReportsCashierView from "./views/ReportsCashierView.svelte";
	import TablesView from "./views/TablesView.svelte";
	import CouriersView from "./views/CouriersView.svelte";
	import StoreSettingsView from "./views/StoreSettingsView.svelte";
	import MasterAdminView from "./views/MasterAdminView.svelte";
	import ResetPasswordView from "./views/ResetPasswordView.svelte";
	import NicheOnboardingWizard from "./components/NicheOnboardingWizard.svelte";
	import CustomModal from "./components/shared/CustomModal.svelte";
	import Alert from "./components/shared/Alert.svelte";
	import Icon from "./components/icons/Icon.svelte";
	import { onMount, onDestroy } from "svelte";
	import { 
		connectSocket, 
		getCurrentStore, 
		fetchOnboardingStatus, 
		getApiBase, 
		clearSession, 
		logout,
		saveSession,
		getCurrentUser, 
		switchOperatorByPin, 
		fetchTeamMembers, 
		getTableReceiptUrl,
		updateOrderStatus,
		safeApiFetch,
		activateSupportKey
	} from "./services/api";

	// Estado SaaS & Autenticação
	let isAuthenticated = false;
	let subscriptionStatus = "active"; // "active", "trial", "past_due", "blocked"
	let storePlan = "MataFome Pro";
	let storeName = "";
	let monthlyPrice = 97.00;
	let activeTab = "admin";
	let openedTabs = new Set(['kanban']);
	$: if (isAuthenticated && activeTab && !['admin', 'reset_password', 'master', 'subscription_blocked'].includes(activeTab)) {
		if (!openedTabs.has(activeTab)) {
			openedTabs.add(activeTab);
			openedTabs = new Set(openedTabs);
		}
	} // Inicia no Login quando não autenticado
	let resetPasswordToken = ""; // Token de recuperação de senha (Fase D)

	// Gestão de Operador Atual & Alternância de Turno (PIN)
	let currentUser = { name: "Proprietário", role: "owner" };
	let showSwitchUserModal = false;
	let switchPinInput = "";
	let switchError = "";
	let isSwitchingOperator = false;
	let quickTeamList = [];
	let selectedMemberForSwitch = null;

	let isBotActive = true;
	let whatsappStatus = "Desconectado";
	let printerStatus = "Desconectada";
	let activeOrdersCount = 0;
	let showOnboardingWizard = false;

	// Alarme Sonoro Contínuo de Novos Pedidos (Padrão iFood / Anota AI)
	let pendingAlarmOrders = [];
	let alarmInterval = null;
	let handleBeforeInstallPrompt = null;

	function startAlarmLoop() {
		if (alarmInterval) return;
		playNotificationSound();
		alarmInterval = setInterval(() => {
			if (pendingAlarmOrders.length > 0) {
				playNotificationSound();
			} else {
				stopAlarmLoop();
			}
		}, 3800);
	}

	function stopAlarmLoop() {
		if (alarmInterval) {
			clearInterval(alarmInterval);
			alarmInterval = null;
		}
	}

	async function handleAcceptAlarmOrder(order) {
		const ordId = order?.order_number || order?.id;
		try {
			await updateOrderStatus(ordId, 'in_prep');
			triggerAlert(`Pedido #${order?.order_number || ordId} aceito! Enviado para a cozinha.`, "success", 4);
			pendingAlarmOrders = pendingAlarmOrders.filter(o => String(o.id) !== String(order.id) && String(o.order_number) !== String(order.order_number));
			if (pendingAlarmOrders.length === 0) {
				stopAlarmLoop();
			}
		} catch (err) {
			triggerAlert(`Erro ao aceitar pedido: ${err.message}`, "error", 4);
		}
	}

	function handleMuteAlarm() {
		stopAlarmLoop();
		triggerAlert("Alarme de novo pedido silenciado nesta sessão.", "info", 3);
	}


	// Alertas
	let showAlert = false;
	let alertMessage = "";
	let alertType = "success";
	let alertTimeToClose = 5;

	function triggerAlert(message, type = "success", timeToClose = 4) {
		alertType = type;
		alertMessage = message;
		alertTimeToClose = timeToClose;
		showAlert = true;
	}

	const closeAlert = () => {
		showAlert = false;
	};

	async function openSwitchUserModal() {
		switchPinInput = "";
		switchError = "";
		selectedMemberForSwitch = null;
		showSwitchUserModal = true;
		try {
			quickTeamList = await fetchTeamMembers();
		} catch (e) {
			console.warn("Erro ao buscar operadores:", e);
		}
	}

	async function handleSwitchOperator(pinToUse) {
		const pin = pinToUse || switchPinInput;
		if (!pin || pin.length < 4) {
			switchError = "Digite seu PIN de 4 dígitos.";
			return;
		}

		isSwitchingOperator = true;
		switchError = "";
		try {
			const res = await switchOperatorByPin(pin);
			if (res && res.user) {
				currentUser = res.user;
				const roleLabel = currentUser.role === 'waiter' ? 'Garçom' : currentUser.role === 'cashier' ? 'Caixa' : 'Dono';
				triggerAlert(`Operador alterado para ${currentUser.name} (${roleLabel})!`, "success", 4);
				showSwitchUserModal = false;
				switchPinInput = "";

				// Role guard check: redireciona caso o módulo atual seja proibido para o novo cargo
				if (currentUser.role === 'waiter' && activeTab !== 'tables') {
					activeTab = 'tables';
				} else if (currentUser.role === 'cashier' && ['menu', 'whatsapp', 'agent_funnel', 'loyalty', 'settings'].includes(activeTab)) {
					activeTab = 'kanban';
				}
			}
		} catch (err) {
			switchError = err.message || "PIN inválido ou usuário inativo.";
			switchPinInput = "";
		} finally {
			isSwitchingOperator = false;
		}
	}

	function appendPinDigit(digit) {
		if (switchPinInput.length < 4) {
			switchPinInput += digit;
			if (switchPinInput.length === 4) {
				handleSwitchOperator(switchPinInput);
			}
		}
	}

	function removeLastPinDigit() {
		if (switchPinInput.length > 0) {
			switchPinInput = switchPinInput.slice(0, -1);
			switchError = "";
		}
	}

	function clearPin() {
		switchPinInput = "";
		switchError = "";
	}

	function handleKeydown(e) {
		if (!showSwitchUserModal) return;
		if (e.key >= '0' && e.key <= '9') {
			appendPinDigit(e.key);
		} else if (e.key === 'Backspace') {
			removeLastPinDigit();
		} else if (e.key === 'Escape') {
			showSwitchUserModal = false;
		} else if (e.key === 'Enter') {
			if (switchPinInput.length >= 4) {
				handleSwitchOperator(switchPinInput);
			}
		}
	}

	// SaaS Route Guard & RBAC
	function handleSelectTab(tab) {
		if (tab === "master") {
			activeTab = "master";
			return;
		}

		if (tab === "logout") {
			handleLogout();
			return;
		}

		if (tab === "login_required") {
			triggerAlert("Acesso restrito: faça login com sua conta ou chave MataFome para acessar os recursos!", "warning", 5);
			activeTab = "admin";
			return;
		}

		if (tab === "subscription_blocked") {
			triggerAlert("Assinatura pendente: regularize sua mensalidade para desbloquear os módulos do restaurante.", "warning", 5);
			activeTab = "subscription_blocked";
			return;
		}

		if (tab.startsWith("role_restricted_")) {
			const role = currentUser?.role || 'operador';
			const roleName = role === 'waiter' ? 'Garçom' : role === 'cashier' ? 'Caixa' : 'Operador';
			triggerAlert(`Acesso restrito: seu perfil de ${roleName} não tem permissão para acessar este módulo.`, "warning", 5);
			return;
		}

		if (tab === "admin") {
			activeTab = "admin";
			return;
		}

		if (!isAuthenticated) {
			triggerAlert("Acesso restrito: faça login para acessar este módulo.", "warning", 4);
			activeTab = "admin";
			return;
		}

		if (subscriptionStatus === "blocked" && tab !== "settings") {
			triggerAlert("Módulos pausados por pendência na assinatura.", "warning", 5);
			activeTab = "subscription_blocked";
			return;
		}

		// Enforce Role Guard
		if (currentUser?.role === 'waiter' && tab !== 'tables') {
			triggerAlert("O perfil Garçom opera exclusivamente no módulo Salão & Mesas.", "info", 4);
			activeTab = 'tables';
			return;
		}

		if (currentUser?.role === 'cashier' && ['menu', 'whatsapp', 'loyalty', 'settings'].includes(tab)) {
			triggerAlert("Este módulo é de acesso exclusivo ao Dono do estabelecimento.", "warning", 4);
			activeTab = 'kanban';
			return;
		}

		activeTab = tab;
	}

	// Login Handler
	function handleLoginSuccess(auth) {
		isAuthenticated = true;
		storeName = auth.store?.name || storeName;
		if (auth.store?.monthly_price) {
			monthlyPrice = parseFloat(auth.store.monthly_price) || 97.00;
		}
		if (auth.user) {
			currentUser = auth.user;
		} else {
			currentUser = getCurrentUser() || { name: auth.store?.name || "Proprietário", role: "owner" };
		}
		try {
			localStorage.setItem("matafome_auth", JSON.stringify(auth));
		} catch (e) {}

		if (auth.token && auth.store) {
			saveSession(auth.token, auth.store, currentUser);
		}
		initStoreSocket();

		// Sincroniza status vindo do backend (fonte primária da verdade)
		const backendSub = auth.store?.subscription_status;
		const subStatus = backendSub || localStorage.getItem("matafome_sub_status") || "active";
		subscriptionStatus = subStatus;
		try {
			localStorage.setItem("matafome_sub_status", subStatus);
		} catch (e) {}

		if (subStatus === "blocked") {
			activeTab = "subscription_blocked";
			triggerAlert(`Bem-vindo, ${storeName}! Sua assinatura está com pagamento pendente. Regularize seu acesso.`, "warning", 6);
		} else {
			if (currentUser.role === 'waiter') {
				activeTab = "tables";
			} else {
				activeTab = "kanban";
			}
			triggerAlert(`Acesso concedido com sucesso! Bem-vindo, ${currentUser.name || storeName}.`, "success", 5);

			// Primeiro Acesso: Abre automaticamente o setup de nichos e complementos
			if (auth.store && (auth.store.onboarding_completed === 0 || auth.store.onboarding_completed === false || auth.store.onboarding_completed === '0')) {
				showOnboardingWizard = true;
			} else {
				fetchOnboardingStatus().then(status => {
					if (status && !status.onboardingCompleted) {
						showOnboardingWizard = true;
					}
				}).catch(() => {});
			}
		}
	}

	// Chave de Ativação / Suporte
	async function handleActivateKey(key) {
		const cleanKey = (key || "").trim().toUpperCase();
		if (!cleanKey) {
			triggerAlert("Por favor, informe a chave de licença.", "warning", 4);
			return { error: "Por favor, informe a chave de licença." };
		}
		try {
			const res = await activateSupportKey(getCurrentStore()?.id, cleanKey);
			if (res && res.success) {
				subscriptionStatus = "active";
				try {
					localStorage.setItem("matafome_sub_status", "active");
					localStorage.setItem("matafome_license_key", cleanKey);
				} catch (e) {}
				triggerAlert(res.message || "Licença validada com sucesso! Sistema 100% desbloqueado.", "success", 6);
				activeTab = currentUser?.role === "waiter" ? "tables" : "kanban";
				return { success: true };
			} else {
				const errMsg = res?.error || "Chave de licença inválida ou inexistente.";
				triggerAlert(errMsg, "error", 5);
				return { error: errMsg };
			}
		} catch (err) {
			const errMsg = err.message || "Erro ao conectar com o servidor para validar a chave.";
			triggerAlert(errMsg, "error", 5);
			return { error: errMsg };
		}
	}

	// Confirmação de Renovação Instantânea PIX
	function handleRenewSuccess() {
		subscriptionStatus = "active";
		try {
			localStorage.setItem("matafome_sub_status", "active");
		} catch (e) {}
		triggerAlert("Pagamento via PIX confirmado! Seu plano MataFome Pro foi renovado com sucesso.", "success", 7);
		activeTab = "kanban";
	}

	// Logout com Invalidação Server-Side (Fase F.2.1)
	async function handleLogout() {
		try {
			await logout();
		} catch (e) {
			console.warn('Erro ao processar logout no servidor:', e);
		}
		isAuthenticated = false;
		storeName = "";
		openedTabs = new Set(['kanban']);
		try {
			localStorage.removeItem("matafome_auth");
			localStorage.removeItem("matafome_token");
			localStorage.removeItem("matafome_store");
			localStorage.removeItem("matafome_sub_status");
		} catch (e) {}
		activeTab = "admin";
		triggerAlert("Você encerrou a sessão no MataFome.", "info", 4);
	}

	// Modal de Confirmação para Retomar Bot
	let statusModalConfirmPlayBot = false;
	function toggleBot() {
		if (isBotActive) {
			isBotActive = false;
			triggerAlert("Robô pausado nesta conversa! Modo de atendimento humano ativado.", "warning", 4);
		} else {
			statusModalConfirmPlayBot = true;
		}
	}

	const handleCancelPlayBot = () => {
		statusModalConfirmPlayBot = false;
	};

	const handleConfirmPlayBot = () => {
		statusModalConfirmPlayBot = false;
		isBotActive = true;
		triggerAlert("Assistente virtual com IA retomado com sucesso!", "success", 4);
	};

	async function handlePrintOrder(order) {
		const orderNum = order?.order_number || order?.id || '';
		const token = localStorage.getItem("matafome_token") || "";
		const selectedPrinter = localStorage.getItem("matafome_selected_printer") || "";
		const silentMode = localStorage.getItem("matafome_silent_print") !== "false";
		const doubleSetting = localStorage.getItem("matafome_double_receipt");
		const rawMode = localStorage.getItem("matafome_print_mode");
		const printMode = (doubleSetting === "false") ? "single" : (rawMode || "double"); // "double" | "single" | "kitchen"

		if (typeof window !== "undefined" && window.desktop?.printSilent && silentMode) {
			try {
				if (printMode === 'double') {
					triggerAlert(`Imprimindo 1ª via (Cozinha) do Pedido #${orderNum}...`, "info", 3);
					const kitchenUrl = `${getApiBase()}/api/orders/${orderNum}/receipt?auto=false&mode=kitchen&width=80&token=${encodeURIComponent(token)}`;
					await window.desktop.printSilent({
						url: kitchenUrl,
						deviceName: selectedPrinter
					});

					// Breve pausa para o hardware da impressora finalizar o corte mecânico da 1ª via
					await new Promise(r => setTimeout(r, 450));

					triggerAlert(`Imprimindo 2ª via (Entrega/Caixa) do Pedido #${orderNum}...`, "success", 4);
					const deliveryUrl = `${getApiBase()}/api/orders/${orderNum}/receipt?auto=false&mode=delivery&width=80&token=${encodeURIComponent(token)}`;
					const res = await window.desktop.printSilent({
						url: deliveryUrl,
						deviceName: selectedPrinter
					});

					if (!res.success) {
						console.warn("[MataFome Impressão] Falha na 2ª via:", res.failureReason);
					}
					return;
				} else {
					triggerAlert(`Imprimindo comanda do Pedido #${orderNum} diretamente na bobina...`, "success", 4);
					const silentPrintUrl = `${getApiBase()}/api/orders/${orderNum}/receipt?auto=false&mode=${printMode}&width=80&token=${encodeURIComponent(token)}`;
					const res = await window.desktop.printSilent({
						url: silentPrintUrl,
						deviceName: selectedPrinter
					});
					if (!res.success) {
						console.warn("[MataFome Impressão] Falha silenciosa:", res.failureReason);
						triggerAlert(`Aviso de impressão: ${res.failureReason}`, "warning", 5);
					}
					return;
				}
			} catch (err) {
				console.warn("[MataFome Impressão] Erro ao disparar impressão silenciosa:", err);
			}
		}
		
		try {
			const webPrintUrl = `${getApiBase()}/api/orders/${orderNum}/receipt?auto=true&mode=${printMode}&width=80&token=${encodeURIComponent(token)}`;
			const printWin = window.open(webPrintUrl, `comanda_${orderNum}`, 'width=420,height=680,menubar=no,toolbar=no,location=no,status=no');
			if (!printWin) {
				window.open(webPrintUrl, '_blank');
			}
			triggerAlert(`Imprimindo comanda do Pedido #${orderNum}...`, "success", 4);
		} catch (err) {
			console.warn("Erro ao abrir comanda:", err);
			triggerAlert(`Falha ao abrir comanda de impressão: ${err.message}`, "warning", 4);
		}
	}

	async function handlePrintTableReceipt({ tableId, type = 'kitchen', items = [], width = 80, itemId = null, changeId = null }) {
		const selectedPrinter = localStorage.getItem("matafome_selected_printer") || "";
		const silentMode = localStorage.getItem("matafome_silent_print") !== "false";
		const labels = {
			kitchen: 'Produção de Cozinha',
			cashier: 'Caixa & Balcão',
			both: 'Cozinha + Caixa (Via Dupla com Corte)',
			pre_bill: 'Conferência / Pré-Conta',
			rectification: 'Retificação de Produção (Cozinha)'
		};
		const label = labels[type] || type;

		if (typeof window !== "undefined" && window.desktop?.printSilent && silentMode) {
			try {
				if (type === 'both') {
					// 1ª via: Cozinha (imprime e corta na guilhotina)
					triggerAlert(`Imprimindo 1ª via (Cozinha) da mesa...`, "info", 3);
					const kitchenUrl = getTableReceiptUrl(tableId, { type: 'kitchen', width, items, itemId, changeId, auto: false });
					await window.desktop.printSilent({
						url: kitchenUrl,
						deviceName: selectedPrinter
					});

					// Pausa mecânica para o acionamento da lâmina da guilhotina
					await new Promise(r => setTimeout(r, 450));

					// 2ª via: Caixa & Balcão (imprime e corta na guilhotina)
					triggerAlert(`Imprimindo 2ª via (Caixa & Balcão) da mesa...`, "success", 4);
					const cashierUrl = getTableReceiptUrl(tableId, { type: 'cashier', width, items, itemId, changeId, auto: false });
					const res = await window.desktop.printSilent({
						url: cashierUrl,
						deviceName: selectedPrinter
					});
					if (!res.success) {
						console.warn("[MataFome Impressão] Falha silenciosa mesa (2ª via):", res.failureReason);
					}
					return;
				} else {
					const silentPrintUrl = getTableReceiptUrl(tableId, { type, width, items, itemId, changeId, auto: false });
					triggerAlert(`Imprimindo cupom (${label}) diretamente na bobina...`, "success", 4);
					const res = await window.desktop.printSilent({
						url: silentPrintUrl,
						deviceName: selectedPrinter
					});
					if (!res.success) {
						console.warn("[MataFome Impressão] Falha silenciosa mesa:", res.failureReason);
						triggerAlert(`Aviso de impressão: ${res.failureReason}`, "warning", 5);
					}
					return;
				}
			} catch (err) {
				console.warn("[MataFome Impressão] Erro na impressão silenciosa de mesa:", err);
			}
		}

		try {
			const webPrintUrl = getTableReceiptUrl(tableId, { type, width, items, itemId, changeId, auto: true });
			const winName = `mesa_${tableId}_${type}_${Date.now()}`;
			const printWin = window.open(webPrintUrl, winName, 'width=420,height=680,menubar=no,toolbar=no,location=no,status=no');
			if (!printWin) {
				window.open(webPrintUrl, '_blank');
			}
			triggerAlert(`Imprimindo cupom (${label})...`, "success", 4);
		} catch (err) {
			console.warn("Erro ao abrir cupom de mesa:", err);
			triggerAlert(`Falha ao abrir impressão: ${err.message}`, "warning", 4);
		}
	}

	async function handleSamplePrint() {
		const storeSlug = getCurrentStore()?.slug || 'cardapio';
		const selectedPrinter = localStorage.getItem("matafome_selected_printer") || "";
		const silentMode = localStorage.getItem("matafome_silent_print") !== "false";

		if (typeof window !== "undefined" && window.desktop?.printSilent && silentMode) {
			try {
				triggerAlert(`Enviando teste de impressão (1ª via Cozinha) para ${selectedPrinter || 'impressora padrão'}...`, "info", 3);
				const silentKitchenUrl = `${getApiBase()}/api/public/${storeSlug}/receipt/sample?auto=false&mode=kitchen&width=80`;
				await window.desktop.printSilent({
					url: silentKitchenUrl,
					deviceName: selectedPrinter
				});

				await new Promise(r => setTimeout(r, 450));

				triggerAlert(`Enviando teste de impressão (2ª via Entrega/Caixa com corte)...`, "success", 4);
				const silentDeliveryUrl = `${getApiBase()}/api/public/${storeSlug}/receipt/sample?auto=false&mode=delivery&width=80`;
				const res = await window.desktop.printSilent({
					url: silentDeliveryUrl,
					deviceName: selectedPrinter
				});
				if (res.success) {
					triggerAlert("Teste de impressão das duas vias enviado com sucesso!", "success", 4);
				} else {
					triggerAlert(`Falha no teste: ${res.failureReason}`, "warning", 5);
				}
				return;
			} catch (err) {
				console.warn("Erro no teste de impressão silenciosa:", err);
			}
		}

		try {
			const webTestUrl = `${getApiBase()}/api/public/${storeSlug}/receipt/sample?auto=true&mode=double&width=80`;
			const win = window.open(webTestUrl, 'sample_receipt_test', 'width=420,height=680,menubar=no,toolbar=no,location=no,status=no');
			if (!win) window.open(webTestUrl, '_blank');
			triggerAlert("Abrindo teste de impressão no navegador...", "info", 4);
		} catch (err) {
			triggerAlert(`Erro ao abrir teste: ${err.message}`, "warning", 4);
		}
	}

	function playNotificationSound() {
		try {
			const ctx = new (window.AudioContext || window.webkitAudioContext)();
			const notes = [
				{ freq: 659.25, time: 0.00, dur: 0.8 }, // E5
				{ freq: 830.61, time: 0.10, dur: 0.9 }, // G#5
				{ freq: 987.77, time: 0.22, dur: 1.2 }  // B5 (Campainha principal)
			];

			notes.forEach(n => {
				const osc = ctx.createOscillator();
				const gain = ctx.createGain();
				osc.type = 'triangle';
				osc.frequency.setValueAtTime(n.freq, ctx.currentTime + n.time);

				gain.gain.setValueAtTime(0.001, ctx.currentTime + n.time);
				gain.gain.exponentialRampToValueAtTime(0.6, ctx.currentTime + n.time + 0.02);
				gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + n.time + n.dur);

				osc.connect(gain);
				gain.connect(ctx.destination);

				osc.start(ctx.currentTime + n.time);
				osc.stop(ctx.currentTime + n.time + n.dur);
			});

			setTimeout(() => {
				try {
					if (ctx && ctx.state !== 'closed') ctx.close();
				} catch (_) {}
			}, 1800);
		} catch (e) {}
	}

	function initStoreSocket() {
		connectSocket(
			(payload) => {
				const ord = payload.order || {};
				const totalVal = parseFloat(ord.total || 0).toFixed(2);
				activeOrdersCount += 1;

				// Inicia o alarme contínuo para pedidos pendentes de aceite
				if (!ord.status || ord.status === 'pending') {
					pendingAlarmOrders = [ord, ...pendingAlarmOrders];
					startAlarmLoop();
				} else {
					playNotificationSound();
				}

				triggerAlert(`Novo pedido #${ord.order_number || ord.id} de ${ord.customer_name || 'Cliente'} (R$ ${totalVal})`, "info", 8);

				const autoPrintEnabled = localStorage.getItem("matafome_auto_print") !== "false";
				if (payload.shouldPrint && autoPrintEnabled) {
					console.log("[MataFome Spooler] Disparando impressão térmica automática da comanda...");
					handlePrintOrder(ord);
				}
			},
			(payload) => {
				const statusLabels = {
					in_prep: 'Em Produção',
					out_for_delivery: 'Saiu para Entrega',
					delivered: 'Concluído / Entregue',
					cancelled: 'Cancelado',
					pending: 'Em Análise'
				};
				const label = statusLabels[payload.status] || payload.status;
				const ordNum = String(payload.orderNumber || payload.orderId || '');

				// Se o pedido aceito/modificado estava na fila de alarme, desliga o alarme
				if (payload.status !== 'pending') {
					pendingAlarmOrders = pendingAlarmOrders.filter(o => String(o.id) !== ordNum && String(o.order_number) !== ordNum);
					if (pendingAlarmOrders.length === 0) {
						stopAlarmLoop();
					}
				}

				triggerAlert(`Pedido #${ordNum} atualizado para "${label}". Cliente notificado via WhatsApp.`, "info", 4);
			},
			(payload) => {
				console.log('💎 [App WebSocket] Assinatura SaaS atualizada:', payload);
				const newSub = payload?.status || payload?.subscriptionStatus;
				if (newSub === 'blocked') {
					subscriptionStatus = 'blocked';
					try { localStorage.setItem('matafome_sub_status', 'blocked'); } catch (e) {}
					activeTab = 'subscription_blocked';
					triggerAlert('Acesso suspenso pelo Administrador Master. Regularize sua mensalidade para continuar.', 'warning', 6);
				} else if (newSub === 'active') {
					subscriptionStatus = 'active';
					try { localStorage.setItem('matafome_sub_status', 'active'); } catch (e) {}
					if (activeTab === 'subscription_blocked') {
						activeTab = currentUser?.role === 'waiter' ? 'tables' : 'kanban';
						triggerAlert('Assinatura ativada com sucesso! Módulos do restaurante liberados.', 'success', 6);
					}
				}
			}
		);
	}

	// PWA: Suporte à Instalação como App no Celular do Garçom / Tablet
	let deferredPwaPrompt = null;
	let showPwaInstallBanner = false;

	function handleInstallPwa() {
		if (deferredPwaPrompt) {
			deferredPwaPrompt.prompt();
			deferredPwaPrompt.userChoice.then((choiceResult) => {
				if (choiceResult.outcome === 'accepted') {
					showPwaInstallBanner = false;
				}
				deferredPwaPrompt = null;
			});
		} else {
			triggerAlert("Para fixar no celular: toque no menu de 3 pontos do navegador e selecione 'Adicionar à tela inicial'.", "info", 6);
			showPwaInstallBanner = false;
		}
	}

	function dismissPwaInstall() {
		showPwaInstallBanner = false;
		try { localStorage.setItem("matafome_waiter_pwa_dismissed", "1"); } catch (_) {}
	}

	onMount(async () => {
		// Interceptor de segurança em tempo real para 403 SUBSCRIPTION_BLOCKED e 401 SESSION_REVOKED
		let isHandling401 = false;
		function handleSessionExpired(reason = "Sua sessão expirou ou foi encerrada. Faça login novamente.") {
			if (isHandling401) return;
			isHandling401 = true;
			isAuthenticated = false;
			storeName = "";
			clearSession();
			try {
				localStorage.removeItem("matafome_auth");
				localStorage.removeItem("matafome_token");
				localStorage.removeItem("matafome_store");
				localStorage.removeItem("matafome_sub_status");
			} catch (e) {}
			activeTab = "admin";
			triggerAlert(reason, "warning", 5);
			setTimeout(() => {
				isHandling401 = false;
			}, 1500);
		}

		if (typeof window !== 'undefined' && window.fetch) {
			const originalFetch = window.fetch;
			window.fetch = async (...args) => {
				const response = await originalFetch(...args);

				if (response.status === 403) {
					try {
						const clone = response.clone();
						const data = await clone.json();
						if (data?.code === 'SUBSCRIPTION_BLOCKED') {
							if (subscriptionStatus !== 'blocked') {
								subscriptionStatus = 'blocked';
								try { localStorage.setItem('matafome_sub_status', 'blocked'); } catch (e) {}
								activeTab = 'subscription_blocked';
								triggerAlert('Sua assinatura foi suspensa pelo Administrador. Regularize o acesso para continuar.', 'warning', 6);
							}
						}
					} catch (_) {}
				}

				if (response.status === 401) {
					const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
					const isAuthCredentialRoute = url.includes('/api/auth/login') ||
						url.includes('/api/auth/login-pin') ||
						url.includes('/api/auth/forgot-password') ||
						url.includes('/api/auth/reset-password') ||
						url.includes('/api/team/quick-switch');

					if (!isAuthCredentialRoute && isAuthenticated) {
						try {
							const clone = response.clone();
							const data = await clone.json();
							const msg = data?.code === 'PASSWORD_CHANGED'
								? 'Sua senha foi alterada. Faça login novamente com sua nova senha.'
								: data?.code === 'SESSION_REVOKED'
								? 'Sua sessão foi encerrada no servidor. Faça login novamente.'
								: (data?.error || 'Sessão expirada. Faça login novamente.');
							handleSessionExpired(msg);
						} catch (_) {
							handleSessionExpired();
						}
					}
				}

				return response;
			};
		}

		// Recupera sessão e status SaaS
		try {
			const savedAuth = localStorage.getItem("matafome_auth");
			const savedSub = localStorage.getItem("matafome_sub_status");
			if (savedAuth) {
				const parsed = JSON.parse(savedAuth);
				isAuthenticated = true;
				storeName = parsed.store?.name || storeName;
				if (parsed.store?.monthly_price) {
					monthlyPrice = parseFloat(parsed.store.monthly_price) || 97.00;
				}
				currentUser = parsed.user || getCurrentUser() || { name: storeName || "Proprietário", role: "owner" };
				if (savedSub === "blocked") {
					subscriptionStatus = "blocked";
					activeTab = "subscription_blocked";
				} else {
					subscriptionStatus = "active";
					activeTab = currentUser.role === "waiter" ? "tables" : "kanban";
				}
			} else {
				isAuthenticated = false;
				activeTab = "admin";
			}
		} catch (e) {
			console.warn("Storage session check skipped:", e);
		}

		// Roteamento de fragmento para Recuperação de Senha (Fase D)
		function checkHashRoute() {
			if (typeof window !== 'undefined') {
				const hash = window.location.hash || '';
				if (hash.startsWith('#/reset-password')) {
					const queryPart = hash.includes('?') ? hash.split('?')[1] : '';
					const params = new URLSearchParams(queryPart);
					const tokenParam = params.get('token');
					if (tokenParam) {
						resetPasswordToken = tokenParam;
						activeTab = 'reset_password';
					}
				}
			}
		}

		checkHashRoute();
		if (typeof window !== 'undefined') {
			window.addEventListener('hashchange', checkHashRoute);
		}

		// Sincronização em Tempo Real com o Backend Multi-Tenant MataFome
		if (isAuthenticated) {
			initStoreSocket();

			// Sincronização da fonte da verdade: valida status atualizado no backend
			try {
				const res = await safeApiFetch('/api/saas/status');
				if (res && res.ok) {
					const data = await res.json();
					const realSub = data.subscriptionStatus || 'active';
					subscriptionStatus = realSub;
					if (data.monthlyPrice) {
						monthlyPrice = parseFloat(data.monthlyPrice) || 97.00;
					}
					try { localStorage.setItem('matafome_sub_status', realSub); } catch (e) {}
					if (realSub === 'blocked') {
						activeTab = 'subscription_blocked';
						triggerAlert('Assinatura suspensa: regularize sua mensalidade para desbloquear os módulos do restaurante.', 'warning', 6);
					} else if (activeTab === 'subscription_blocked' && realSub === 'active') {
						activeTab = currentUser.role === 'waiter' ? 'tables' : 'kanban';
					}
				}
			} catch (subErr) {
				console.warn('Verificação de status SaaS no servidor indisponível:', subErr.message);
			}
		}

		// Verifica se o restaurante já fez a parametrização inicial
		try {
			const status = await fetchOnboardingStatus();
			if (status && !status.onboardingCompleted) {
				showOnboardingWizard = true;
			}
		} catch (e) {
			console.warn("Onboarding check skipped:", e);
		}

		// PWA: Registro de Service Worker e Captura de Instalação na Tela Inicial
		if (typeof window !== 'undefined') {
			if ('serviceWorker' in navigator) {
				navigator.serviceWorker.register('./sw.js').catch(() => {});
			}

			const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
			const isDismissed = localStorage.getItem("matafome_waiter_pwa_dismissed") === "1";

			handleBeforeInstallPrompt = (e) => {
				e.preventDefault();
				deferredPwaPrompt = e;
				if (!isStandalone && !isDismissed) {
					showPwaInstallBanner = true;
				}
			};
			window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
		}
	});

	onDestroy(() => {
		stopAlarmLoop();
		if (typeof window !== 'undefined') {
			window.removeEventListener('hashchange', checkHashRoute);
			if (handleBeforeInstallPrompt) {
				window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
			}
		}
	});

	let isMobileSidebarOpen = false;

	function getTabLabel(tab) {
		switch (tab) {
			case 'kanban': return 'Pedidos';
			case 'menu': return 'Cardápio';
			case 'tables': return 'Mesas & Salão';
			case 'couriers': return 'Entregadores';
			case 'whatsapp': return 'Atendimento IA';
			case 'funnel': return 'Funil do Agente';
			case 'loyalty': return 'Fidelidade & Cupons';
			case 'cashier': return 'Frente de Caixa';
			case 'reports_cashier': return 'Relatórios';
			case 'printer': return 'Impressão';
			case 'settings': return 'Configurações';
			default: return 'Painel';
		}
	}
</script>

<svelte:window on:keydown={handleKeydown} />

<div class="app-container">
	<!-- 1. Barra de Título Superior (Apenas após o login e fora do modo Master) -->
	{#if isAuthenticated && activeTab !== "admin" && activeTab !== "master"}
		<Titlebar
			title="MataFome Desktop"
			{whatsappStatus}
			{printerStatus}
			showQuickStatus={activeTab !== "admin" && activeTab !== "subscription_blocked"}
		/>
	{/if}

	<!-- Alarme Flutuante de Novo Pedido (Padrão iFood / Anota AI) -->
	{#if isAuthenticated && pendingAlarmOrders.length > 0}
		{@const currentAlarmOrder = pendingAlarmOrders[0]}
		<div class="order-alarm-floating-bar" role="alert">
			<div class="alarm-bell-icon">🔔</div>
			<div class="alarm-info-box">
				<strong>NOVO PEDIDO #{currentAlarmOrder.order_number || currentAlarmOrder.id}!</strong>
				<span>
					{currentAlarmOrder.customer_name || 'Cliente'} • R$ {parseFloat(currentAlarmOrder.total || 0).toFixed(2).replace('.', ',')}
					{#if currentAlarmOrder.source === 'web_menu'}
						<span class="alarm-source-badge web">Cardápio Digital</span>
					{:else}
						<span class="alarm-source-badge wpp">WhatsApp</span>
					{/if}
					{#if pendingAlarmOrders.length > 1}
						<b>(+{pendingAlarmOrders.length - 1} aguardando)</b>
					{/if}
				</span>
			</div>
			<div class="alarm-btn-group">
				<button type="button" class="btn-alarm-accept" on:click={() => handleAcceptAlarmOrder(currentAlarmOrder)}>
					✓ Aceitar Pedido
				</button>
				<button type="button" class="btn-alarm-mute" on:click={handleMuteAlarm} title="Silenciar som">
					Silenciar
				</button>
			</div>
		</div>
	{/if}

	<div class="app-body">
		<!-- 2. Barra Lateral de Navegação (Aparece apenas após o login e fora dos modos de Login, Master e Bloqueio) -->
		{#if isAuthenticated && activeTab !== "admin" && activeTab !== "master" && activeTab !== "subscription_blocked"}
			<Sidebar
				{activeTab}
				onSelectTab={handleSelectTab}
				{activeOrdersCount}
				{isAuthenticated}
				isSubscriptionActive={subscriptionStatus !== "blocked"}
				{storeName}
				{currentUser}
				{whatsappStatus}
				{printerStatus}
				onOpenSwitchUser={openSwitchUserModal}
				isOpenMobile={isMobileSidebarOpen}
				onCloseMobile={() => isMobileSidebarOpen = false}
			/>
		{/if}

		<main class="main-content" on:scroll={(e) => { e.currentTarget.scrollTop = 0; e.currentTarget.scrollLeft = 0; }}>
			<!-- Barra de Topo Mobile (visível apenas em telas < 768px com Sidebar ativa) -->
			{#if isAuthenticated && activeTab !== "admin" && activeTab !== "master" && activeTab !== "subscription_blocked"}
				<div class="mobile-nav-bar">
					<button 
						type="button" 
						class="btn-hamburger" 
						on:click={() => isMobileSidebarOpen = true}
						aria-label="Abrir Menu de Navegação"
					>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
							<line x1="3" y1="12" x2="21" y2="12"></line>
							<line x1="3" y1="6" x2="21" y2="6"></line>
							<line x1="3" y1="18" x2="21" y2="18"></line>
						</svg>
					</button>
					<div class="mobile-bar-title">
						<span class="mobile-brand-name">Mata<span class="highlight">Fome</span></span>
						<span class="mobile-tab-pill">{getTabLabel(activeTab)}</span>
					</div>
				</div>
			{/if}

			<!-- Banner PWA de Instalação na Tela Inicial (Garçom / Mobile) -->
			{#if showPwaInstallBanner}
				<div class="waiter-pwa-banner">
					<div class="pwa-text-box">
						<strong>📲 Instalar App do Garçom / Salão</strong>
						<span>Abra em tela cheia com 1 toque no celular sem a barra do navegador.</span>
					</div>
					<div class="pwa-btn-group">
						<button type="button" class="btn-pwa-no" on:click={dismissPwaInstall}>Depois</button>
						<button type="button" class="btn-pwa-yes" on:click={handleInstallPwa}>📲 Instalar</button>
					</div>
				</div>
			{/if}

			<!-- Alerta Global Flutuante -->
			{#if showAlert}
				<div class="global-alert">
					<Alert
						type={alertType}
						message={alertMessage}
						timeToClose={alertTimeToClose}
						onClose={closeAlert}
					/>
				</div>
			{/if}

			{#if activeTab === "admin"}
				<LoginView
					onLoginSuccess={handleLoginSuccess}
					onOpenMaster={() => activeTab = "master"}
				/>
			{:else if activeTab === "reset_password"}
				<ResetPasswordView
					token={resetPasswordToken}
					onGoToLogin={() => {
						resetPasswordToken = "";
						if (typeof window !== 'undefined') window.location.hash = "";
						activeTab = "admin";
					}}
				/>
			{:else if activeTab === "master"}
				<MasterAdminView
					onBack={() => {
						activeTab = isAuthenticated ? "kanban" : "admin";
					}}
				/>
			{:else if activeTab === "subscription_blocked"}
				<SubscriptionBlockedView
					{storeName}
					planName={storePlan}
					{monthlyPrice}
					onActivateKey={handleActivateKey}
					onRenewSuccess={handleRenewSuccess}
					onLogout={handleLogout}
				/>
			{:else}
				<!-- ABAS OPERACIONAIS EM CACHE PERSISTENTE (ZERO SEGUNDOS DE ESPERA AO NAVEGAR) -->
				<div class="view-pane" class:active-pane={activeTab === "kanban"}>
					<KanbanView
						onPrintOrder={handlePrintOrder}
						onPrintTableReceipt={handlePrintTableReceipt}
						onNotifyClient={(order, msg) => triggerAlert(msg, "success", 4)}
						bind:activeOrdersCount
					/>
				</div>

				{#if openedTabs.has("tables")}
					<div class="view-pane" class:active-pane={activeTab === "tables"}>
						<TablesView
							onNotify={(msg, type) => triggerAlert(msg, type)}
							onPrintOrder={handlePrintOrder}
							onPrintTableReceipt={handlePrintTableReceipt}
						/>
					</div>
				{/if}

				{#if openedTabs.has("couriers")}
					<div class="view-pane" class:active-pane={activeTab === "couriers"}>
						<CouriersView
							onNotify={(msg, type) => triggerAlert(msg, type)}
						/>
					</div>
				{/if}

				{#if openedTabs.has("whatsapp")}
					<div class="view-pane" class:active-pane={activeTab === "whatsapp"}>
						<WhatsAppView
							{isBotActive}
							onNotify={(msg, type) => triggerAlert(msg, type)}
							bind:whatsappStatus
						/>
					</div>
				{/if}

				{#if openedTabs.has("agent_funnel")}
					<div class="view-pane" class:active-pane={activeTab === "agent_funnel"}>
						<AgentFunnelView
							onNotify={(msg, type) => triggerAlert(msg, type)}
						/>
					</div>
				{/if}

				{#if openedTabs.has("printer")}
					<div class="view-pane" class:active-pane={activeTab === "printer"}>
						<PrinterView
							onPrintSample={handleSamplePrint}
						/>
					</div>
				{/if}

				{#if openedTabs.has("menu")}
					<div class="view-pane" class:active-pane={activeTab === "menu"}>
						<MenuView
							onNotify={(msg, type) => triggerAlert(msg, type)}
						/>
					</div>
				{/if}

				{#if openedTabs.has("loyalty")}
					<div class="view-pane" class:active-pane={activeTab === "loyalty"}>
						<LoyaltyView
							onNotify={(msg, type) => triggerAlert(msg, type)}
						/>
					</div>
				{/if}

				{#if openedTabs.has("cashier")}
					<div class="view-pane" class:active-pane={activeTab === "cashier"}>
						<CashierView
							onNotify={(msg, type) => triggerAlert(msg, type)}
						/>
					</div>
				{/if}

				{#if openedTabs.has("reports")}
					<div class="view-pane" class:active-pane={activeTab === "reports"}>
						<ReportsCashierView
							onNotify={(msg, type) => triggerAlert(msg, type)}
						/>
					</div>
				{/if}

				{#if openedTabs.has("settings")}
					<div class="view-pane" class:active-pane={activeTab === "settings"}>
						<StoreSettingsView
							onNotify={(msg, type) => triggerAlert(msg, type)}
							{subscriptionStatus}
							{monthlyPrice}
						/>
					</div>
				{/if}
			{/if}
		</main>
	</div>

	<!-- Modal de Confirmação para Retomar o Robô -->
	{#if statusModalConfirmPlayBot}
		<CustomModal
			colorTheme="primary"
			confirmButtonLabel="Sim, retomar bot"
			title="Confirmar Retomada do Bot IA"
			onCancel={handleCancelPlayBot}
			onContinue={handleConfirmPlayBot}
		>
			<div slot="body">
				<p>Você tem certeza que deseja finalizar o atendimento manual?</p>
				<p style="color: #64748B; font-size: 14px; margin-top: 8px;">
					Ao continuar, o assistente virtual com IA reassumirá automaticamente as mensagens deste cliente no WhatsApp.
				</p>
			</div>
		</CustomModal>
	{/if}

	<!-- Modal de Primeiro Acesso (Onboarding Wizard com Nichos e Complementos) -->
	{#if showOnboardingWizard}
		<div class="onboarding-overlay">
			<div class="onboarding-modal-box">
				<NicheOnboardingWizard
					onNotify={(msg, type) => triggerAlert(msg, type)}
					onComplete={() => {
						showOnboardingWizard = false;
						triggerAlert("Configurações iniciais salvas com sucesso! Bem-vindo ao MataFome.", "success", 5);
					}}
				/>
			</div>
		</div>
	{/if}

	<!-- Modal de Troca Rápida de Operador por PIN (Seguro, estilo POS / Terminal bancário) -->
	{#if showSwitchUserModal}
		<div class="pin-modal-overlay" on:click|self={() => showSwitchUserModal = false}>
			<div class="pin-modal-box">
				<div class="pin-modal-header">
					<div class="pin-header-icon">
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
							<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
							<path d="M7 11V7a5 5 0 0 1 10 0v4"/>
						</svg>
					</div>
					<div class="pin-header-text">
						<h3>Terminal Protegido</h3>
						<p>Autenticação rápida por PIN numérico</p>
					</div>
					<button class="btn-pin-close" on:click={() => showSwitchUserModal = false} title="Fechar">
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
							<line x1="18" y1="6" x2="6" y2="18"></line>
							<line x1="6" y1="6" x2="18" y2="18"></line>
						</svg>
					</button>
				</div>

				<div class="pin-modal-body">
					<!-- Indicador de Status do Terminal -->
					<div class="terminal-security-badge">
						<span class="security-dot"></span>
						<span>Acesso restrito à equipe autorizada</span>
					</div>

					<!-- Mostrador dos 4 Dígitos do PIN -->
					<div class="pin-display-wrapper">
						<div class="pin-dots {switchError ? 'has-error' : ''}">
							{#each [0, 1, 2, 3] as idx}
								<span class="pin-dot {switchPinInput.length > idx ? 'filled' : ''}"></span>
							{/each}
						</div>
					</div>

					{#if switchError}
						<div class="pin-error-msg">
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
								<circle cx="12" cy="12" r="10"/>
								<line x1="12" y1="8" x2="12" y2="12"/>
								<line x1="12" y1="16" x2="12.01" y2="16"/>
							</svg>
							<span>{switchError}</span>
						</div>
					{/if}

					<!-- Teclado Numérico Compacto e Escuro -->
					<div class="pin-keypad">
						{#each [1, 2, 3, 4, 5, 6, 7, 8, 9] as num}
							<button 
								class="btn-pin-key" 
								type="button"
								on:click={() => appendPinDigit(String(num))}
								disabled={isSwitchingOperator}
							>
								{num}
							</button>
						{/each}
						<button 
							class="btn-pin-key btn-clear" 
							type="button"
							on:click={clearPin}
							title="Limpar tudo"
							disabled={isSwitchingOperator}
						>
							C
						</button>
						<button 
							class="btn-pin-key" 
							type="button"
							on:click={() => appendPinDigit('0')}
							disabled={isSwitchingOperator}
						>
							0
						</button>
						<button 
							class="btn-pin-key btn-backspace" 
							type="button"
							on:click={removeLastPinDigit}
							title="Apagar dígito"
							disabled={isSwitchingOperator}
						>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
								<path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"></path>
								<line x1="18" y1="9" x2="12" y2="15"></line>
								<line x1="12" y1="9" x2="18" y2="15"></line>
							</svg>
						</button>
					</div>

					<div class="pin-modal-hint">
						{#if isSwitchingOperator}
							<span class="verifying-text">Autenticando...</span>
						{:else}
							<span>Use o teclado físico ou numpad da tela</span>
						{/if}
					</div>
				</div>
			</div>
		</div>
	{/if}
</div>

<style>
	.app-container {
		display: flex;
		flex-direction: column;
		width: 100vw;
		height: 100vh;
		height: 100dvh;
		background: var(--neutral-bg, #F8FAFC);
		overflow: hidden;
		user-select: none;
	}

	.app-body {
		display: flex;
		flex: 1;
		height: calc(100vh - 32px);
		height: calc(100dvh - 32px);
		overflow: hidden;
	}

	.main-content {
		flex: 1;
		min-height: 0;
		min-width: 0;
		height: 100%;
		position: relative;
		overflow: hidden;
		background: var(--neutral-bg, #F8FAFC);
		display: flex;
		flex-direction: column;
	}

	/* Top bar no mobile para acionar drawer */
	.mobile-nav-bar {
		display: none;
	}

	@media (max-width: 767px) {
		.mobile-nav-bar {
			display: flex;
			align-items: center;
			gap: 12px;
			height: 44px;
			padding: 0 12px;
			background: #0F1218;
			border-bottom: 1px solid rgba(255, 255, 255, 0.08);
			z-index: 40;
			flex-shrink: 0;
			user-select: none;
		}

		.btn-hamburger {
			width: 36px;
			height: 36px;
			background: rgba(255, 255, 255, 0.06);
			border: 1px solid rgba(255, 255, 255, 0.1);
			border-radius: var(--radius-sm, 6px);
			color: #FFFFFF;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			padding: 0;
			transition: background 0.15s ease;
		}

		.btn-hamburger:hover {
			background: rgba(255, 255, 255, 0.12);
		}

		.mobile-bar-title {
			display: flex;
			align-items: center;
			gap: 10px;
		}

		.mobile-brand-name {
			font-size: 14px;
			font-weight: 700;
			color: #FFFFFF;
			letter-spacing: -0.2px;
		}

		.mobile-brand-name .highlight {
			color: var(--brand-primary, #FF6A00);
		}

		.mobile-tab-pill {
			font-size: 11px;
			font-weight: 600;
			background: rgba(255, 106, 0, 0.15);
			color: #FF8A3D;
			border: 1px solid rgba(255, 106, 0, 0.3);
			padding: 2px 8px;
			border-radius: var(--radius-sm, 6px);
			text-transform: uppercase;
			letter-spacing: 0.3px;
		}
	}

	.global-alert {
		position: absolute;
		top: 16px;
		right: 16px;
		z-index: 10000;
		min-width: 340px;
		animation: slideInDown 0.25s ease;
	}

	@keyframes slideInDown {
		from { opacity: 0; transform: translateY(-8px); }
		to { opacity: 1; transform: translateY(0); }
	}

	.onboarding-overlay {
		position: fixed;
		inset: 0;
		background: rgba(15, 23, 42, 0.75);
		backdrop-filter: blur(6px);
		z-index: 9999;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 24px;
	}

	.onboarding-modal-box {
		width: 100%;
		max-width: 960px;
		max-height: 92vh;
		background: #0E121A;
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 20px;
		box-shadow: 0 25px 70px -15px rgba(0, 0, 0, 0.9), 0 0 40px rgba(255, 106, 0, 0.12);
		overflow: hidden;
		display: flex;
		flex-direction: column;
		color: #FFFFFF;
	}

	/* MODAL DE TROCA DE OPERADOR POR PIN */
	.pin-modal-overlay {
		position: fixed;
		inset: 0;
		background: rgba(11, 14, 20, 0.82);
		backdrop-filter: blur(8px);
		z-index: 10000;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 16px;
	}

	.pin-modal-box {
		width: 100%;
		max-width: 320px;
		background: #11141B;
		border: 1px solid rgba(255, 255, 255, 0.1);
		border-radius: 20px;
		box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.8), 0 0 30px rgba(255, 106, 0, 0.08);
		overflow: hidden;
		display: flex;
		flex-direction: column;
		color: #FFFFFF;
		animation: popIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
	}

	@keyframes popIn {
		from { opacity: 0; transform: scale(0.95); }
		to { opacity: 1; transform: scale(1); }
	}

	.pin-modal-header {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 18px 20px 14px 20px;
		border-bottom: 1px solid rgba(255, 255, 255, 0.06);
		position: relative;
	}

	.pin-header-icon {
		width: 38px;
		height: 38px;
		border-radius: 10px;
		background: rgba(255, 106, 0, 0.12);
		color: #FF6A00;
		border: 1px solid rgba(255, 106, 0, 0.25);
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.pin-header-text h3 {
		margin: 0;
		font-size: 14px;
		font-weight: 700;
		color: #FFFFFF;
		letter-spacing: -0.2px;
	}

	.pin-header-text p {
		margin: 2px 0 0 0;
		font-size: 11px;
		color: #94A3B8;
	}

	.btn-pin-close {
		position: absolute;
		top: 14px;
		right: 14px;
		background: none;
		border: none;
		color: #64748B;
		cursor: pointer;
		padding: 6px;
		border-radius: 8px;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.15s;
	}

	.btn-pin-close:hover {
		color: #FFFFFF;
		background: rgba(255, 255, 255, 0.06);
	}

	.pin-modal-body {
		padding: 20px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;
	}

	.terminal-security-badge {
		display: flex;
		align-items: center;
		gap: 6px;
		background: rgba(255, 255, 255, 0.04);
		border: 1px solid rgba(255, 255, 255, 0.06);
		padding: 4px 10px;
		border-radius: 9999px;
		font-size: 10.5px;
		color: #94A3B8;
		font-weight: 500;
	}

	.security-dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: #10B981;
		box-shadow: 0 0 6px rgba(16, 185, 129, 0.6);
	}

	.pin-display-wrapper {
		display: flex;
		flex-direction: column;
		align-items: center;
		margin: 4px 0;
	}

	.pin-dots {
		display: flex;
		gap: 16px;
		padding: 12px 28px;
		background: #0B0E14;
		border: 1px solid rgba(255, 255, 255, 0.08);
		border-radius: 14px;
		box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4);
	}

	.pin-dots.has-error {
		animation: shake 0.35s ease;
		border-color: rgba(239, 68, 68, 0.5);
	}

	@keyframes shake {
		0%, 100% { transform: translateX(0); }
		20%, 60% { transform: translateX(-6px); }
		40%, 80% { transform: translateX(6px); }
	}

	.pin-dot {
		width: 12px;
		height: 12px;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.12);
		transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
	}

	.pin-dot.filled {
		background: #FF6A00;
		box-shadow: 0 0 12px rgba(255, 106, 0, 0.8);
		transform: scale(1.25);
	}

	.pin-error-msg {
		display: flex;
		align-items: center;
		gap: 6px;
		color: #F87171;
		font-size: 11.5px;
		font-weight: 600;
		background: rgba(239, 68, 68, 0.1);
		border: 1px solid rgba(239, 68, 68, 0.25);
		padding: 6px 12px;
		border-radius: 8px;
		text-align: center;
	}

	.pin-keypad {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 8px;
		width: 100%;
		max-width: 240px;
	}

	.btn-pin-key {
		height: 46px;
		background: rgba(255, 255, 255, 0.04);
		border: 1px solid rgba(255, 255, 255, 0.07);
		border-radius: 12px;
		color: #FFFFFF;
		font-size: 19px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.12s ease;
		display: flex;
		align-items: center;
		justify-content: center;
		font-family: inherit;
	}

	.btn-pin-key:hover {
		background: rgba(255, 255, 255, 0.09);
		border-color: rgba(255, 255, 255, 0.18);
	}

	.btn-pin-key:active {
		background: rgba(255, 106, 0, 0.15);
		border-color: #FF6A00;
		transform: scale(0.96);
	}

	.btn-pin-key.btn-clear {
		color: #94A3B8;
		font-size: 14px;
		font-weight: 700;
	}

	.btn-pin-key.btn-clear:hover {
		color: #EF4444;
		background: rgba(239, 68, 68, 0.1);
		border-color: rgba(239, 68, 68, 0.3);
	}

	.btn-pin-key.btn-backspace {
		color: #94A3B8;
	}

	.btn-pin-key.btn-backspace:hover {
		color: #FFFFFF;
	}

	.pin-modal-hint {
		font-size: 11px;
		color: #64748B;
		text-align: center;
	}

	.verifying-text {
		color: #FF6A00;
		font-weight: 600;
	}

	/* Banner PWA para Garçom / Celular */
	.waiter-pwa-banner {
		position: fixed;
		bottom: 16px;
		left: 16px;
		right: 16px;
		max-width: 480px;
		margin: 0 auto;
		background: #0F172A;
		color: #FFFFFF;
		border-radius: 14px;
		padding: 12px 16px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		box-shadow: 0 10px 25px -5px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1);
		z-index: 99999;
	}
	.waiter-pwa-banner .pwa-text-box {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}
	.waiter-pwa-banner .pwa-text-box strong {
		font-size: 13.5px;
		font-weight: 700;
		color: #FFFFFF;
	}
	.waiter-pwa-banner .pwa-text-box span {
		font-size: 11.5px;
		color: #94A3B8;
		line-height: 1.3;
	}
	.waiter-pwa-banner .pwa-btn-group {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
	}
	.waiter-pwa-banner .btn-pwa-no {
		background: transparent;
		border: 1px solid rgba(255,255,255,0.2);
		color: #CBD5E1;
		padding: 6px 12px;
		border-radius: 8px;
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
	}
	.waiter-pwa-banner .btn-pwa-yes {
		background: #EA580C;
		border: none;
		color: #FFFFFF;
		padding: 6px 14px;
		border-radius: 8px;
		font-size: 12px;
		font-weight: 700;
		cursor: pointer;
	}

	/* Banner Flutuante de Alarme Contínuo de Novo Pedido (Padrão iFood / Anota AI) */
	.order-alarm-floating-bar {
		position: fixed;
		top: 44px;
		left: 50%;
		transform: translateX(-50%);
		width: calc(100% - 32px);
		max-width: 720px;
		background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
		color: #FFFFFF;
		border-radius: 14px;
		padding: 12px 18px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 14px;
		box-shadow: 0 12px 30px rgba(239, 68, 68, 0.45), 0 0 0 2px rgba(255, 255, 255, 0.2);
		z-index: 99998;
		animation: pulse-alarm-bar 1.8s infinite;
	}

	@keyframes pulse-alarm-bar {
		0% {
			box-shadow: 0 10px 25px rgba(239, 68, 68, 0.4), 0 0 0 0 rgba(239, 68, 68, 0.7);
		}
		70% {
			box-shadow: 0 14px 35px rgba(239, 68, 68, 0.6), 0 0 0 10px rgba(239, 68, 68, 0);
		}
		100% {
			box-shadow: 0 10px 25px rgba(239, 68, 68, 0.4), 0 0 0 0 rgba(239, 68, 68, 0);
		}
	}

	.alarm-bell-icon {
		font-size: 26px;
		animation: swing-bell 0.8s ease-in-out infinite alternate;
		flex-shrink: 0;
	}

	@keyframes swing-bell {
		0% { transform: rotate(-15deg); }
		100% { transform: rotate(15deg); }
	}

	.alarm-info-box {
		display: flex;
		flex-direction: column;
		gap: 2px;
		flex: 1;
		min-width: 0;
	}

	.alarm-info-box strong {
		font-size: 15px;
		font-weight: 800;
		letter-spacing: 0.3px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.alarm-info-box span {
		font-size: 12.5px;
		color: #FEE2E2;
		display: flex;
		align-items: center;
		gap: 6px;
		flex-wrap: wrap;
	}

	.alarm-source-badge {
		font-size: 10px;
		font-weight: 800;
		padding: 2px 6px;
		border-radius: 4px;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		background: rgba(0, 0, 0, 0.25);
	}

	.alarm-source-badge.web {
		background: #2563EB;
		color: #FFF;
	}

	.alarm-source-badge.wpp {
		background: #16A34A;
		color: #FFF;
	}

	.alarm-btn-group {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
	}

	.btn-alarm-accept {
		background: #FFFFFF;
		color: #DC2626;
		border: none;
		padding: 8px 16px;
		border-radius: 8px;
		font-size: 13px;
		font-weight: 800;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 5px;
		box-shadow: 0 4px 10px rgba(0,0,0,0.15);
		transition: all 0.15s ease;
	}

	.btn-alarm-accept:hover {
		background: #FEF2F2;
		transform: scale(1.03);
	}

	.btn-alarm-mute {
		background: rgba(0, 0, 0, 0.2);
		color: #FFFFFF;
		border: 1px solid rgba(255, 255, 255, 0.3);
		padding: 8px 12px;
		border-radius: 8px;
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
		transition: background 0.15s;
	}

	.btn-alarm-mute:hover {
		background: rgba(0, 0, 0, 0.35);
	}
	.view-pane {
		display: none;
		width: 100%;
		height: 100%;
		min-height: 0;
		min-width: 0;
		flex: 1;
		flex-direction: column;
	}

	.view-pane.active-pane {
		display: flex;
	}

</style>
